import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trash2, Truck, CheckCircle, Clock, BadgeCheck, ArrowRight, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { trackBite } from '../components/Analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesPageV2 = () => {
  const [services, setServices] = useState([]);
  const [serviceLevels, setServiceLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDefinitions, setShowDefinitions] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, levelsRes] = await Promise.all([
        fetch(`${API_URL}/api/services/`),
        fetch(`${API_URL}/api/service-levels/`)
      ]);
      
      if (servicesRes.ok) {
        const data = await servicesRes.json();
        setServices(data);
      }
      if (levelsRes.ok) {
        const data = await levelsRes.json();
        setServiceLevels(data);
      }
    } catch (err) {
      console.error('Failed to fetch services:', err);
    } finally {
      setLoading(false);
    }
  };

  const getServiceLevels = (serviceId) => {
    return serviceLevels.filter(l => l.service_id === serviceId).sort((a, b) => a.display_order - b.display_order);
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'Trash2': return <Trash2 className="w-8 h-8" />;
      case 'Truck': return <Truck className="w-8 h-8" />;
      default: return <Trash2 className="w-8 h-8" />;
    }
  };

  const getBookingUrl = (service) => {
    return service.slug === 'waste_removal' ? '/book/waste' : '/book/delivery';
  };

  const getButtonLabel = (service) => {
    return service.slug === 'waste_removal' ? 'Book Waste Removal' : 'Book Material Delivery';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0D9488]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0D9488] to-[#0F766E] py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Our Services
          </h1>
          <p className="text-lg text-[#CCFBF1] max-w-2xl mx-auto">
            Simple logistics solutions for Melbourne tradespeople. We handle the legwork so you can focus on your next job.
          </p>
        </div>
      </section>

      {/* Definitions Accordion */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <button
          onClick={() => setShowDefinitions(!showDefinitions)}
          className="w-full flex items-center justify-between bg-[#F0FDFA] border border-[#99F6E4] rounded-lg p-4 text-left"
        >
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-[#0D9488]" />
            <span className="font-medium text-[#134E4A]">Definitions: Client vs Customer</span>
          </div>
          {showDefinitions ? (
            <ChevronUp className="w-5 h-5 text-[#0D9488]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#0D9488]" />
          )}
        </button>
        
        {showDefinitions && (
          <div className="bg-white border border-[#E2E8F0] border-t-0 rounded-b-lg p-6 -mt-1">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-[#111111] mb-2">Client</h4>
                <p className="text-[#64748B] text-sm">
                  The installer or trades service provider who books and pays for the service. That's you!
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-[#111111] mb-2">Customer</h4>
                <p className="text-[#64748B] text-sm">
                  The end user, homeowner, or site owner where waste is collected or materials are delivered. Transa coordinates directly with the customer on your behalf.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Services Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {services.map((service) => {
            const levels = getServiceLevels(service.id);
            return (
              <Card key={service.id} className="border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow bg-white overflow-hidden">
                <CardContent className="p-0">
                  {/* Service Header */}
                  <div className={`p-6 ${service.slug === 'waste_removal' ? 'bg-gradient-to-r from-[#0D9488] to-[#14B8A6]' : 'bg-gradient-to-r from-[#0F766E] to-[#0D9488]'}`}>
                    <div className="flex items-center gap-4 text-white mb-3">
                      {getIcon(service.icon)}
                      <h2 className="text-xl font-bold">{service.name}</h2>
                    </div>
                    <p className="text-[#CCFBF1] text-sm">{service.short_description}</p>
                  </div>
                  
                  {/* Service Levels */}
                  <div className="p-6">
                    <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wide mb-4">Service Levels</h3>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {levels.map((level) => (
                        <span
                          key={level.id}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                            level.slug === 'priority' 
                              ? 'bg-[#FEF3C7] text-[#D97706]' 
                              : level.slug === 'subscription'
                                ? 'bg-[#DBEAFE] text-[#2563EB]'
                                : 'bg-[#F0FDFA] text-[#0D9488]'
                          }`}
                        >
                          {level.name}
                        </span>
                      ))}
                    </div>
                    
                    {/* Pricing Summary */}
                    <div className="space-y-3 mb-6">
                      {levels.map((level) => (
                        <div key={level.id} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-[#64748B]" />
                            <span className="text-[#374151]">{level.name}:</span>
                            <span className="text-[#64748B]">{level.sla_label}</span>
                          </div>
                          <span className="font-semibold text-[#0D9488]">
                            {level.billing_type === 'WEEKLY_SUBSCRIPTION' 
                              ? `$${level.weekly_price}/week`
                              : `from $${level.base_price}`
                            }
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Key Features */}
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center gap-2 text-sm text-[#374151]">
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                        <span>Transa coordinates with customer directly</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#374151]">
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                        <span>You get notified when job is complete</span>
                      </div>
                      {service.slug === 'waste_removal' && (
                        <div className="flex items-center gap-2 text-sm text-[#F59E0B]">
                          <BadgeCheck className="w-4 h-4" />
                          <span>Pallets not included (extra charges apply)</span>
                        </div>
                      )}
                    </div>

                    {/* CTA Button */}
                    <Link to={getBookingUrl(service)} onClick={() => trackBite(`book_${service.slug}_click`, '/services', service.slug)}>
                      <Button className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white h-12 text-base font-medium">
                        {getButtonLabel(service)}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#111111] text-center mb-12">How It Works</h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'You Book', desc: 'Select your service and enter details via our simple form.' },
              { step: '2', title: 'We Coordinate', desc: 'Transa contacts the customer directly to arrange timing.' },
              { step: '3', title: 'We Deliver', desc: 'Our team completes the pickup or delivery as scheduled.' },
              { step: '4', title: 'You\'re Notified', desc: 'You receive confirmation with photo proof when done.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#0D9488] text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-[#111111] mb-2">{item.title}</h3>
                <p className="text-sm text-[#64748B]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#F0FDFA] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#134E4A] mb-4">Ready to Get Started?</h2>
          <p className="text-[#0F766E] mb-8">
            Book your first service today and see how Transa can help streamline your logistics.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book/waste">
              <Button className="bg-[#0D9488] hover:bg-[#0F766E] text-white h-12 px-8">
                <Trash2 className="w-5 h-5 mr-2" />
                Book Waste Removal
              </Button>
            </Link>
            <Link to="/book/delivery">
              <Button variant="outline" className="border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488] hover:text-white h-12 px-8">
                <Truck className="w-5 h-5 mr-2" />
                Book Material Delivery
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPageV2;
